import React, { Suspense } from "react";
import {
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useRouteError,
} from "react-router-dom";
import { AcquisitionAttributionTracker } from "components/common/AcquisitionAttributionTracker";
import { ErrorHandler } from "components/common/ErrorHandler";
import { PageViewTracker } from "components/screen-view/PageViewTracker";
import { ScreenplayLeaveBlocker } from "features/script-editor/ScreenplayLeaveBlocker";
import { AppearanceProvider } from "providers/appearance";
import { BackgroundTasksProvider } from "providers/background-tasks";
import { BackgroundTasksIndicator } from "providers/background-tasks/BackgroundTasksIndicator";
import { GlobalAiRunWatcher } from "providers/background-tasks/GlobalAiRunWatcher";
import { UserPreferencesProvider } from "providers/user-preferences";
import RouteFallback from "routes/RouteFallback";
import appRoutes from "./routes/config";

const PageViewTrackerGate = () => {
  const location = useLocation();
  if (location.pathname.startsWith("/s/")) {
    return null;
  }
  return <PageViewTracker />;
};

/**
 * Every route renders inside this layout. A data router, rather than `BrowserRouter`, so the
 * screenplay editor can hold a navigation until its waiting edit is saved (`useBlocker`).
 */
const AppRootLayout: React.FC = () => (
  // Preferences/appearance use useAxios → useAuth → useNavigate, so they must sit inside the
  // router. Mounting them in index.tsx AppShell crashes the tree before routes render.
  <UserPreferencesProvider>
    <AppearanceProvider>
      {/* Above the routes so in-flight background tasks survive navigation. */}
      <BackgroundTasksProvider>
        <GlobalAiRunWatcher />
        <BackgroundTasksIndicator />
        <AcquisitionAttributionTracker />
        <PageViewTrackerGate />
        <ScreenplayLeaveBlocker />
        <ErrorHandler>
          <Suspense fallback={<RouteFallback label="Loading…" containerClassName="min-h-screen" />}>
            <Outlet />
          </Suspense>
        </ErrorHandler>
      </BackgroundTasksProvider>
    </AppearanceProvider>
  </UserPreferencesProvider>
);

/**
 * Route errors are caught by `ErrorHandler` inside the layout. An error from the layout's own
 * providers fails the app as it did before the data router, instead of the router's default
 * error screen.
 */
const RethrowRouteError = () => {
  throw useRouteError();
};

// No loaders, actions or fetchers run in this app, so the data-router flags change nothing
// but the console warnings. `appRoutes` ends with a `*` route, so every URL matches.
const router = createBrowserRouter(
  [{ element: <AppRootLayout />, errorElement: <RethrowRouteError />, children: appRoutes }],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);

export const Router: React.FC = () => (
  <RouterProvider router={router} future={{ v7_startTransition: true }} />
);
